/* =====================================================================
   Soul of Papers — product landing interactivity (vanilla, no deps)
   - gallery view switching
   - quantity stepper (wired to the product form quantity input)
   - WebAudio "signature song" preview synth (play/pause/progress/waveform)
   - dancing paper cast toggle while the song plays
   - video review carousel
   - estimated delivery date range
   Re-inits on Shopify theme-editor section load.
   ===================================================================== */
(function () {
  'use strict';

  /* ---------- HERO ---------- */
  function initHero(root) {
    if (!root || root.dataset.sopInit === '1') return;
    root.dataset.sopInit = '1';

    var stage = root.querySelector('[data-sop-stage]');
    var views = Array.prototype.slice.call(root.querySelectorAll('[data-sop-view]'));
    var thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-sop-thumb]'));

    // gallery view switching
    function show(idx) {
      views.forEach(function (v) {
        var hide = String(v.dataset.sopView) !== String(idx);
        v.hidden = hide;
        // pause any video when its view is hidden so audio doesn't keep playing
        if (hide) {
          var vid = v.querySelector('video');
          if (vid && !vid.paused) { try { vid.pause(); } catch (e) {} }
        }
      });
      thumbs.forEach(function (t) { t.classList.toggle('is-active', String(t.dataset.sopThumb) === String(idx)); });
    }
    thumbs.forEach(function (t) {
      t.addEventListener('click', function () { show(t.dataset.sopThumb); });
    });
    if (thumbs.length) show(thumbs[0].dataset.sopThumb);

    // quantity stepper
    var qtyInput = root.querySelector('[data-sop-qty-input]');
    root.querySelectorAll('[data-sop-qty-dec]').forEach(function (b) {
      b.addEventListener('click', function () {
        var v = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
        qtyInput.value = v; qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
    root.querySelectorAll('[data-sop-qty-inc]').forEach(function (b) {
      b.addEventListener('click', function () {
        var v = Math.max(1, (parseInt(qtyInput.value, 10) || 1) + 1);
        qtyInput.value = v; qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });

    // bundle quantity tiers — click a tier to set the quantity (discounts auto-apply at checkout)
    var bundleTiers = Array.prototype.slice.call(root.querySelectorAll('[data-sop-bundle-tier]'));
    if (bundleTiers.length && qtyInput) {
      var selectTier = function (btn) {
        bundleTiers.forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('is-selected', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        var q = Math.max(1, parseInt(btn.getAttribute('data-sop-bundle-qty'), 10) || 1);
        qtyInput.value = q; qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      };
      bundleTiers.forEach(function (b) { b.addEventListener('click', function () { selectTier(b); }); });
      var preTier = bundleTiers.filter(function (b) { return b.classList.contains('is-selected'); })[0];
      if (preTier) {
        var pq = Math.max(1, parseInt(preTier.getAttribute('data-sop-bundle-qty'), 10) || 1);
        qtyInput.value = pq;
      }
    }

    // estimated delivery dates
    var from = root.querySelector('[data-sop-deliver-from]');
    var to = root.querySelector('[data-sop-deliver-to]');
    if (from || to) {
      var minD = parseInt(root.dataset.sopDeliverMin, 10) || 5;
      var maxD = parseInt(root.dataset.sopDeliverMax, 10) || 9;
      if (from) from.textContent = fmtDate(minD);
      if (to) to.textContent = fmtDate(maxD);
    }

    // waveform bars (build once)
    var waveWrap = root.querySelector('[data-sop-wave]');
    if (waveWrap && !waveWrap.childElementCount) {
      for (var i = 0; i < 40; i++) {
        var h = 6 + Math.round(20 * Math.abs(Math.sin(i * 0.7) + 0.35 * Math.sin(i * 1.9)));
        h = Math.min(26, Math.max(6, h));
        var s = document.createElement('span');
        s.className = 'wv';
        s.style.height = h + 'px';
        s.style.animationDuration = (0.5 + (i % 5) * 0.08).toFixed(2) + 's';
        s.style.animationDelay = (i * 0.035).toFixed(2) + 's';
        waveWrap.appendChild(s);
      }
    }

    // song preview synth
    setupSynth(root, stage);

    // live variant → price / availability / form id
    setupVariants(root);

    // sticky add-to-cart: show only when the main ATC button is out of view
    setupStickyAtc(root);
  }

  /* ---------- VARIANTS: live price + availability on variant change ---------- */
  function setupVariants(root) {
    var dataEl = root.querySelector('[data-sop-variants]');
    if (!dataEl) return;
    var variants;
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }

    function setBtnState(btn, available) {
      if (!btn) return;
      var lbl = btn.querySelector('[data-sop-atc-label]');
      if (available) {
        btn.disabled = false;
        btn.removeAttribute('aria-disabled');
        if (lbl && btn.dataset.sopAtcAvail != null) lbl.textContent = btn.dataset.sopAtcAvail;
      } else {
        btn.disabled = true;
        btn.setAttribute('aria-disabled', 'true');
        if (lbl && btn.dataset.sopAtcSold != null) lbl.textContent = btn.dataset.sopAtcSold;
      }
    }

    function apply(id) {
      id = String(id);
      var v = variants[id];
      if (!v) {
        // option combination has no matching variant -> block buying
        root.querySelectorAll('[data-sop-main-atc]').forEach(function (b) { setBtnState(b, false); });
        return;
      }
      // keep every product form (add to cart / checkout / legacy) on the chosen variant
      root.querySelectorAll('[name="id"]').forEach(function (inp) { inp.value = id; });

      // price blocks (price + compare + save badge)
      root.querySelectorAll('[data-sop-price-root]').forEach(function (pr) {
        var pEl = pr.querySelector('[data-sop-price]');
        if (pEl) pEl.textContent = v.price;
        var cEl = pr.querySelector('[data-sop-compare]');
        var sEl = pr.querySelector('[data-sop-save]');
        var saveMap = {};
        var sm = pr.querySelector('[data-sop-save-map]');
        if (sm) { try { saveMap = JSON.parse(sm.textContent); } catch (e) {} }
        if (v.compare) {
          if (cEl) { cEl.textContent = v.compare; cEl.hidden = false; }
          if (sEl) { if (saveMap[id]) { sEl.textContent = saveMap[id]; sEl.hidden = false; } else { sEl.hidden = true; } }
        } else {
          if (cEl) cEl.hidden = true;
          if (sEl) sEl.hidden = true;
        }
      });

      // sticky bar price
      var sp = root.querySelector('[data-sop-sticky-price]'); if (sp) sp.textContent = v.price;
      var sc = root.querySelector('[data-sop-sticky-compare]');
      if (sc) { if (v.compare) { sc.textContent = v.compare; sc.hidden = false; } else { sc.hidden = true; } }

      // availability on every add-to-cart button
      root.querySelectorAll('[data-sop-main-atc]').forEach(function (b) { setBtnState(b, v.available); });
    }

    // Source 1 — legacy single <select name="id"> inside the combined block
    root.querySelectorAll('[data-sop-variant-select]').forEach(function (sel) {
      sel.addEventListener('change', function () { apply(sel.value); });
    });

    // Source 2 — standalone Variant picker block (per-option dropdowns or pills)
    var picker = root.querySelector('[data-sop-variant-picker]');
    if (picker) {
      var accent = picker.getAttribute('data-sop-accent') || '';
      var ink = picker.getAttribute('data-sop-ink') || '';
      var groups = Array.prototype.slice.call(picker.querySelectorAll('[data-sop-opt-group]'));

      function currentValues() {
        return groups.map(function (g) {
          var sel = g.querySelector('[data-sop-opt-select]');
          if (sel) return sel.value;
          var active = g.querySelector('.sop-vp-pill.is-active');
          return active ? active.getAttribute('data-sop-opt-value') : null;
        });
      }
      function matchVariant(vals) {
        var ids = Object.keys(variants);
        for (var i = 0; i < ids.length; i++) {
          var opts = variants[ids[i]].options || [];
          var ok = true;
          for (var j = 0; j < vals.length; j++) {
            if (String(opts[j]) !== String(vals[j])) { ok = false; break; }
          }
          if (ok) return ids[i];
        }
        return null;
      }
      function onChange() {
        var id = matchVariant(currentValues());
        if (id) apply(id);
        else root.querySelectorAll('[data-sop-main-atc]').forEach(function (b) { setBtnState(b, false); });
      }
      groups.forEach(function (g) {
        var sel = g.querySelector('[data-sop-opt-select]');
        if (sel) sel.addEventListener('change', onChange);
        g.querySelectorAll('.sop-vp-pill').forEach(function (pill) {
          pill.addEventListener('click', function () {
            g.querySelectorAll('.sop-vp-pill').forEach(function (p) {
              var on = p === pill;
              p.classList.toggle('is-active', on);
              p.setAttribute('aria-pressed', on ? 'true' : 'false');
              p.style.background = on ? accent : '#fff';
              p.style.color = on ? '#fff' : ink;
            });
            onChange();
          });
        });
      });
    }
  }

  function setupStickyAtc(root) {
    var bar = root.querySelector('[data-sop-sticky-atc]');
    var mainBtn = root.querySelector('[data-sop-main-atc]');
    if (!bar || !mainBtn) return;
    bar.removeAttribute('hidden');
    function show(on) { bar.classList.toggle('is-visible', on); }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          // main button visible -> hide sticky; scrolled past (above viewport) -> show
          if (e.isIntersecting) { show(false); }
          else { show(e.boundingClientRect.top < 0); }
        });
      }, { threshold: 0 });
      io.observe(mainBtn);
    } else {
      window.addEventListener('scroll', function () {
        var r = mainBtn.getBoundingClientRect();
        show(r.bottom < 0);
      }, { passive: true });
    }
    // reflect main button state (loading / disabled) on the sticky button
    var stickyBtn = bar.querySelector('button');
    if (stickyBtn && mainBtn) {
      var mo = new MutationObserver(function () {
        stickyBtn.disabled = mainBtn.disabled || mainBtn.getAttribute('aria-disabled') === 'true';
      });
      mo.observe(mainBtn, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
    }
  }

  function fmtDate(offset) {
    var d = new Date(); d.setDate(d.getDate() + offset);
    var day = d.getDate();
    var v = day % 100;
    var suf = (['th', 'st', 'nd', 'rd'][(v - 20) % 10] || ['th', 'st', 'nd', 'rd'][v] || 'th');
    var wd = d.toLocaleDateString('en-US', { weekday: 'long' });
    var mo = d.toLocaleDateString('en-US', { month: 'long' });
    return wd + ', ' + mo + ' ' + day + suf;
  }

  function setupSynth(root, stage) {
    var btn = root.querySelector('[data-sop-play]');
    if (!btn) return;
    var iconPlay = root.querySelector('[data-sop-icon-play]');
    var iconPause = root.querySelector('[data-sop-icon-pause]');
    var progEl = root.querySelector('[data-sop-progress]');
    var timeEl = root.querySelector('[data-sop-time]');

    var root_hz = parseFloat(root.dataset.sopRoot) || 261.63;
    var tempo = parseFloat(root.dataset.sopTempo) || 132;
    var pattern = (root.dataset.sopPattern || '0,4,7,9,7,4,7,9,4,7,9,7')
      .split(',').map(function (n) { return parseInt(n, 10) || 0; });

    var ctx = null, master = null, step = 0, interval = null, raf = null, playStart = 0, playing = false;
    var SPAN = 30000; // 30s loop

    function ensureCtx() {
      if (!ctx) {
        var AC = window.AudioContext || window.webkitAudioContext;
        if (!AC) return false;
        ctx = new AC();
        master = ctx.createGain();
        master.gain.value = 0.0001;
        master.connect(ctx.destination);
      }
      if (ctx.state === 'suspended') ctx.resume();
      return true;
    }
    function voice(freq, t, dur, type, vol) {
      var o = ctx.createOscillator(); o.type = type; o.frequency.value = freq;
      var g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(vol, t + 0.015);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      o.connect(g); g.connect(master); o.start(t); o.stop(t + dur + 0.05);
    }
    function tick() {
      var t = ctx.currentTime + 0.02;
      var scale = [0, 2, 4, 7, 9];
      var deg = pattern[step % pattern.length];
      var semis = scale[deg % 5] + 12 * Math.floor(deg / 5);
      voice(root_hz * Math.pow(2, semis / 12), t, 0.55, 'triangle', 0.22);
      if (step % 2 === 0) voice(root_hz * Math.pow(2, (semis + 7) / 12) / 2, t, 0.5, 'sine', 0.10);
      if (step % 4 === 0) voice(root_hz / 2, t, 0.9, 'sine', 0.16);
      step++;
    }
    function loop() {
      if (!playing) return;
      var t = (performance.now() - playStart) % SPAN;
      if (progEl) progEl.style.width = (t / SPAN) * 100 + '%';
      if (timeEl) timeEl.textContent = '0:' + String(Math.floor(t / 1000)).padStart(2, '0');
      raf = requestAnimationFrame(loop);
    }
    function clearTimers() {
      if (interval) { clearInterval(interval); interval = null; }
      if (raf) { cancelAnimationFrame(raf); raf = null; }
    }
    function setPlaying(on) {
      playing = on;
      if (iconPlay) iconPlay.hidden = on;
      if (iconPause) iconPause.hidden = !on;
      btn.setAttribute('aria-label', on ? 'Pause preview' : 'Play preview');
      if (stage) stage.classList.toggle('sop-dance', on);
    }
    function play() {
      if (!ensureCtx()) return;
      clearTimers();
      var now = ctx.currentTime;
      master.gain.cancelScheduledValues(now);
      master.gain.setValueAtTime(Math.max(0.0001, master.gain.value), now);
      master.gain.linearRampToValueAtTime(0.85, now + 0.08);
      step = 0; tick();
      interval = setInterval(tick, 60000 / tempo / 2);
      playStart = performance.now();
      raf = requestAnimationFrame(loop);
      setPlaying(true);
    }
    function pause() {
      clearTimers();
      if (ctx && master) {
        var now = ctx.currentTime;
        master.gain.cancelScheduledValues(now);
        master.gain.setValueAtTime(master.gain.value, now);
        master.gain.linearRampToValueAtTime(0.0001, now + 0.12);
      }
      setPlaying(false);
    }
    btn.addEventListener('click', function () { playing ? pause() : play(); });
    root._sopStopSynth = pause;
  }

  /* ---------- REVIEWS video carousel ---------- */
  function initReviews(root) {
    if (!root || root.dataset.sopInit === '1') return;
    root.dataset.sopInit = '1';
    var track = root.querySelector('[data-sop-vidtrack]');
    if (!track) return;
    var prev = root.querySelector('[data-sop-vid-prev]');
    var next = root.querySelector('[data-sop-vid-next]');
    if (prev) prev.addEventListener('click', function () { track.scrollBy({ left: -512, behavior: 'smooth' }); });
    if (next) next.addEventListener('click', function () { track.scrollBy({ left: 512, behavior: 'smooth' }); });
    // click a poster to play its inline video
    root.querySelectorAll('[data-sop-vidcard]').forEach(function (card) {
      var overlay = card.querySelector('[data-sop-vidoverlay]');
      if (overlay) overlay.style.cursor = 'pointer';
      card.addEventListener('click', function () {
        var v = card.querySelector('video');
        if (!v || card.dataset.sopPlaying === '1') return;
        card.dataset.sopPlaying = '1';
        var frame = card.querySelector('.frame') || card;
        // hide every overlay element (poster, play icon, gradient, badge, caption)
        // — done via inline display:none so it beats their inline display styles
        Array.prototype.forEach.call(frame.children, function (el) {
          if (el.tagName !== 'VIDEO') el.style.display = 'none';
        });
        v.hidden = false;
        v.removeAttribute('hidden');
        v.setAttribute('controls', '');
        v.style.display = 'block';
        var p = v.play();
        if (p && p.catch) { p.catch(function () {}); }
      });
    });
  }

  /* ---------- boot ---------- */
  function boot(scope) {
    (scope || document).querySelectorAll('[data-sop-hero]').forEach(initHero);
    (scope || document).querySelectorAll('[data-sop-reviews]').forEach(initReviews);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }
  // Shopify theme editor: re-init the section that (re)loaded
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var h = e.target.querySelector('[data-sop-hero]');
    if (h && typeof h._sopStopSynth === 'function') h._sopStopSynth();
  });
})();
